import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';
import moment from 'moment';
import get from 'lodash/get';
import template from 'lodash/template';

import Alert from '@glu/alerts-react';
import { Button, PRIMARY, SECONDARY, TERTIARY } from '@glu/buttons-react';

import { ALERTVARIATIONS, DATE_FORMAT, WORKSPACES } from 'constants.js';
import usePlaceResolver, { variants } from 'components/usePlaceResolver/usePlaceResolver';
import ModalNotification from 'components/modalNotification/ModalNotification';
import useLocale from 'components/useLocale';
import { retrieveACHImportMap } from 'api/payments/service';
import { deleteACHImportMap } from 'api/wireImports/service';

import useStyles from './ViewACHImportMap.styles';
import DetailsTable from './DetailsTable';
import { MAP_FORMAT } from '../CreateACHImportMap/utils.mapFormats';

const { WARNING } = ALERTVARIATIONS;

const YES_OR_NO = (locale) => ({
  true: locale['common.yes'],
  false: locale['common.no']
});

function ViewACHImportMapComponent({ closeModal, history, mapCode }) {
  const classes = useStyles();
  const locale = useLocale();
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const yesOrNoOptions = useMemo(() => YES_OR_NO(locale), [locale]);
  const services = useMemo(() => [retrieveACHImportMap(mapCode)], [mapCode]);
  const prevUrl = history?.location?.state?.previous || WORKSPACES.PYMTCNTR;

  const { Placeholder, results } = usePlaceResolver({
    services,
    variant: variants.spinner
  });

  const closeDeleteModalFunc = useCallback(() => setShowDeleteModal(false), [setShowDeleteModal]);
  const showDeleteModalFunc = useCallback(() => setShowDeleteModal(true), [setShowDeleteModal]);

  const onModify = useCallback(
    () =>
      history.push({
        pathname: `/payments/managePaymentMap/modify/ach/${mapCode}`,
        state: { previous: prevUrl }
      }),
    [mapCode, history, prevUrl]
  );

  const onCancel = useCallback(() => closeModal(), [closeModal]);

  const { achImportMapDetails: detailsData, achImportMapHeader: headerData } = get(
    results,
    '[0].data',
    { achImportMapHeader: {}, achImportMapDetails: {} }
  );
  const {
    createdBy,
    delimiter,
    endOfRecordMethod,
    lastUsedBy,
    lastUsedDate,
    mapName,
    mapType,
    paymentType,
    processMode,
    recordLength,
    skipFirstRecord,
    stripQuotes,
    templateType,
    zeroAmountFields
  } = headerData;

  const mapFormat = mapType?.toUpperCase();

  const onDeleteMap = () =>
    deleteACHImportMap({ mapCode }).subscribe(({ data, success }) => {
      if (!success) {
        const { code, userMessage } = data?.[0] || {};
        closeDeleteModalFunc();
        return setError(locale[`wireImports.mapErrors.${code}`] || `${code}: ${userMessage}`);
      }

      return history.push({
        pathname: prevUrl,
        state: {
          messages: [
            {
              id: 1,
              title: locale['payments.manageImportMaps.delete.succeeded'],
              data: { mapName },
              success: true
            }
          ]
        }
      });
    });

  const endOfRecordMethodField = useMemo(() => {
    if (endOfRecordMethod === 'NEWLINE') {
      return 'NEW LINE';
    }
    return endOfRecordMethod;
  }, [endOfRecordMethod]);

  if (Placeholder) {
    return <Placeholder />;
  }

  return (
    <div className={classes.wrapper}>
      <ModalNotification
        variant="error"
        show={showDeleteModal}
        onHide={closeDeleteModalFunc}
        suppressIcon={false}
        data-qa="delete-ach-map-modal"
      >
        <div className="modalWrapper">
          <div className={classnames('deleteWarning', classes.modalHeader)}>
            {locale['payments.manageImportMaps.delete.warning'].toUpperCase()}
          </div>
          <div className={classes.modalBody}>
            <div className="deleteContent">
              {template(locale['payments.manageImportMaps.delete.content'])({
                mapName
              })}
            </div>
            <div className="deleteConfirm">
              {locale['wireImports.deleteMapDoYouWishToProceed']}
            </div>
          </div>
          <Button
            dataQa="continue"
            className="btn btn-primary"
            variant={PRIMARY}
            onClick={onDeleteMap}
          >
            {locale['common.continue']}
          </Button>

          <Button className="btn btn-tertiary" variant={TERTIARY} onClick={closeDeleteModalFunc}>
            {locale['common.cancel']}
          </Button>
        </div>
      </ModalNotification>

      <h1 className={classes.title}>
        {template(locale['wireImports.viewImportMap'])({
          determinedFor: locale['wireImports.forACH']
        })}
      </h1>

      {error && (
        <Alert
          dataQa="ach-import-map-view-alert"
          className={classes.alert}
          variant={WARNING}
          title={locale['wireImports.canNotDeleteMap']}
          message={error}
          onHide={() => setError(null)}
        />
      )}

      <section className={classes.topSection}>
        <h2 className={classes.sectionTitle}>{locale['wireImports.mapDetails']}</h2>
        <div className={classes.details}>
          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['payments.manageImportMaps.name']}
            </div>
            <div className={classnames(classes.valueWrapper)}>{mapName || '-'}</div>
          </div>

          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['payments.manageImportMaps.paymentType']}
            </div>
            <div className={classnames(classes.valueWrapper)}>{paymentType || '-'}</div>
          </div>

          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['payments.manageImportMaps.mapType']}
            </div>
            <div className={classnames(classes.valueWrapper)}>
              {locale[`wireImports.${processMode}`] || '-'}
            </div>
          </div>

          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['wireImports.mapFormat']}
            </div>
            <div className={classnames(classes.valueWrapper)}>{mapType || '-'}</div>
          </div>

          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['payments.manageImportMaps.createdBy']}
            </div>
            <div className={classnames(classes.valueWrapper)}>{createdBy || '-'}</div>
          </div>

          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['payments.manageImportMaps.lastUsedDate']}
            </div>
            <div className={classnames(classes.valueWrapper)}>
              {lastUsedDate ? moment(lastUsedDate).format(DATE_FORMAT) : '-'}
            </div>
          </div>

          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['payments.manageImportMaps.lastUsedBy']}
            </div>
            <div className={classnames(classes.valueWrapper)}>{lastUsedBy || '-'}</div>
          </div>
        </div>
      </section>
      <section className={classes.topSection}>
        <h2 className={classes.sectionTitle}>{locale['wireImports.mapParameters']}</h2>
        <div className={classes.details}>
          {recordLength ? (
            <div className={classes.detailWrapper}>
              <div className={classnames(classes.label, classes.labelWrapper)}>
                {locale['wireImports.recordLength']}
              </div>
              <div className={classnames(classes.valueWrapper)}>{recordLength || '-'}</div>
            </div>
          ) : null}
          {delimiter ? (
            <div className={classes.detailWrapper}>
              <div className={classnames(classes.label, classes.labelWrapper)}>
                {locale['payments.manageImportMaps.delimiter']}
              </div>
              <div className={classnames(classes.valueWrapper)}>{delimiter || '-'}</div>
            </div>
          ) : null}
          {endOfRecordMethod ? (
            <div className={classes.detailWrapper}>
              <div className={classnames(classes.label, classes.labelWrapper)}>
                {locale['payments.manageImportMaps.endOfRecordMethod']}
              </div>
              <div className={classnames(classes.valueWrapper)}>
                {endOfRecordMethodField || '-'}
              </div>
            </div>
          ) : null}
          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['payments.manageImportMaps.skipHeaderRecord']}
            </div>
            <div className={classnames(classes.valueWrapper)}>
              {yesOrNoOptions[String(skipFirstRecord)] || skipFirstRecord}
            </div>
          </div>
          {mapFormat === MAP_FORMAT.DELIMITED && (
            <div className={classes.detailWrapper}>
              <div className={classnames(classes.label, classes.labelWrapper)}>
                {locale['payments.manageImportMaps.stripQuotes']}
              </div>
              <div className={classnames(classes.valueWrapper)}>
                {yesOrNoOptions[String(stripQuotes)]}
              </div>
            </div>
          )}
          <div className={classes.detailWrapper}>
            <div className={classnames(classes.label, classes.labelWrapper)}>
              {locale['wireImports.LoadZero']}
            </div>
            <div className={classnames(classes.valueWrapper)}>
              {yesOrNoOptions[String(zeroAmountFields)] || zeroAmountFields}
            </div>
          </div>
        </div>
      </section>

      <DetailsTable
        processMode={processMode}
        mapFormat={mapFormat}
        templateType={templateType}
        data={detailsData}
      />

      <div className={classes.buttons}>
        {templateType === 'Custom' && (
          <>
            <Button
              dataQa="delete-ach-wire-import-map"
              className="btn btn-primary"
              variant={PRIMARY}
              onClick={showDeleteModalFunc}
            >
              {locale['common.delete']}
            </Button>

            <Button className="btn secondary" variant={SECONDARY} onClick={onModify}>
              {locale['common.modify']}
            </Button>
          </>
        )}

        <Button className="btn btn-tertiary" variant={TERTIARY} onClick={onCancel}>
          {locale['common.close']}
        </Button>
      </div>
    </div>
  );
}

ViewACHImportMapComponent.propTypes = {
  mapCode: PropTypes.string.isRequired,
  history: PropTypes.shape({}).isRequired,
  closeModal: PropTypes.func.isRequired
};

const Routed = withRouter(ViewACHImportMapComponent);
export default Routed;
